import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Inbox as InboxIcon, Check, X, ArrowUpRight, Send } from "lucide-react";
import { toast } from "sonner";

export default function Inbox() {
  const [tab, setTab] = useState("received");
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);

  const load = async () => {
    const [r, s] = await Promise.all([
      api.get("/connections/inbox").then(x => x.data).catch(() => []),
      api.get("/connections/sent").then(x => x.data).catch(() => []),
    ]);
    setReceived(r);
    setSent(s);
  };
  useEffect(() => { load(); }, []);

  const respond = async (id, action) => {
    await api.post(`/connections/${id}/respond?action=${action}`);
    toast.success(action === "accept" ? "Invite accepted" : "Invite declined");
    load();
  };

  const list = tab === "received" ? received : sent;
  const empty = tab === "received" ? "No invites yet." : "You haven't sent any invites.";

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10" data-testid="inbox-page">
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Network</div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2 flex items-center gap-3">
        <InboxIcon size={36} style={{ color: "var(--association-theme)" }} />
        Inbox
      </h1>

      {/* Tabs */}
      <div className="mt-8 flex gap-2 border-b border-white/10">
        <TabBtn active={tab==="received"} count={received.length}
                onClick={() => setTab("received")} label="Received" tid="tab-received" />
        <TabBtn active={tab==="sent"} count={sent.length}
                onClick={() => setTab("sent")} label="Sent" tid="tab-sent" />
      </div>

      <div className="mt-6 space-y-3" data-testid="invite-list">
        {list.length === 0 && (
          <div className="cs-card rounded-2xl p-10 text-center text-white/50 text-sm">{empty}</div>
        )}
        {list.map((inv) => (
          <div key={inv.id} className="cs-card rounded-2xl p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <Link to={`/profile/${tab==="received" ? inv.from_user_id : inv.to_user_id}`}
                        className="font-display text-lg uppercase tracking-tight hover:underline">
                    {tab==="received" ? inv.from_user_name : inv.to_user_name}
                  </Link>
                  <StatusPill status={inv.status} />
                  {inv.role_being_offered && (
                    <span className="text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
                      Offering · {inv.role_being_offered}
                    </span>
                  )}
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mt-1">
                  {new Date(inv.created_at).toLocaleString()}
                </div>
                <p className="mt-3 text-sm text-white/75 whitespace-pre-wrap">{inv.message}</p>
              </div>
              {tab==="received" && inv.status === "pending" && (
                <div className="flex flex-col gap-2 shrink-0">
                  <Button onClick={() => respond(inv.id, "accept")} className="btn-themed h-9 px-4 rounded-full uppercase tracking-[0.2em] text-[10px]"
                          data-testid={`accept-${inv.id}`}>
                    <Check size={13} className="mr-1.5" /> Accept
                  </Button>
                  <Button onClick={() => respond(inv.id, "decline")} variant="outline"
                          className="h-9 px-4 rounded-full uppercase tracking-[0.2em] text-[10px] bg-transparent border-white/20 hover:bg-white hover:text-black"
                          data-testid={`decline-${inv.id}`}>
                    <X size={13} className="mr-1.5" /> Decline
                  </Button>
                </div>
              )}
              {tab==="sent" && (
                <Link to={`/profile/${inv.to_user_id}`} className="text-xs text-white/50 hover:text-white inline-flex items-center gap-1">
                  Open <ArrowUpRight size={12} />
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TabBtn({ active, count, onClick, label, tid }) {
  return (
    <button onClick={onClick} data-testid={tid}
      className={`px-4 py-3 text-xs uppercase tracking-[0.22em] border-b-2 transition ${
        active ? "border-[color:var(--association-theme)] text-white" : "border-transparent text-white/55 hover:text-white"
      }`}>
      {label} <span className="ml-1 text-white/40">({count})</span>
    </button>
  );
}

function StatusPill({ status }) {
  const map = {
    pending:  { bg: "bg-yellow-500/15", fg: "text-yellow-400", text: "Pending" },
    accepted: { bg: "bg-emerald-500/15", fg: "text-emerald-400", text: "Accepted" },
    declined: { bg: "bg-red-500/15", fg: "text-red-400", text: "Declined" },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`text-[9px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full ${s.bg} ${s.fg}`}>
      {s.text}
    </span>
  );
}
